import {
  Component, Input, Output, EventEmitter,
  ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { AiService, ChatMessage } from '../services/ai-service.service';
import { Equipment, EquipmentGroupResponse } from '../models/equipment.model';

export interface EquipmentGroup {
  type: string;
  bestMatch: Equipment | null;
  alternativeCount: number;
  whyNeeded?: string;
}

export interface ProjectRecommendationCard {
  projectType: string;
  projectTitle: string;
  groups: EquipmentGroup[];
  location?: string;
  recommendationSummary?: string;
  recommendedCount?: number;
  availableCount?: number;
}

export interface ProjectRefinementData {
  duration: string;
  soilType: string;
  emirate: string;
  operatorRequired: string;
  fuelIncluded: string;
}

export interface RefinedRecommendationResponse {
  primaryEquipment: string[];
  supportEquipment: string[];
  estimatedCostRange: string;
  reasoning: string;
}

export interface PendingProjectContext {
  projectType: string;
  rawQuery: string;
}

export interface AiMessage extends ChatMessage {
  recommendationCard?: ProjectRecommendationCard;
  showRefinementCard?: boolean;
  refinementSubmitted?: boolean;
  refinedResponse?: RefinedRecommendationResponse;
  missingFields?: { duration: boolean; soilType: boolean };
  awaitingLocation?: PendingProjectContext;
}

// Emirate keywords for detection
const EMIRATE_KEYWORDS: Record<string, string> = {
  'dubai': 'Dubai',
  'abu dhabi': 'Abu Dhabi',
  'abudhabi': 'Abu Dhabi',
  'sharjah': 'Sharjah',
  'ajman': 'Ajman',
  'ras al khaimah': 'Ras Al Khaimah',
  'rak': 'Ras Al Khaimah',
  'fujairah': 'Fujairah',
  'umm al quwain': 'Umm Al Quwain',
  'uaq': 'Umm Al Quwain',
  'uae': 'UAE',
  'anywhere': 'UAE',
};

function extractEmirateFromText(text: string): string | null {
  const lower = text.toLowerCase();
  for (const [kw, emirate] of Object.entries(EMIRATE_KEYWORDS)) {
    if (lower.includes(kw)) return emirate;
  }
  return null;
}

function parseRefinedResponse(text: string): RefinedRecommendationResponse {
  const primaryMatch = text.match(/primary equipment[:\s\n]+([^\n]+(?:\n[-•]\s*[^\n]+)*)/i);
  const supportMatch = text.match(/support equipment[:\s\n]+([^\n]+(?:\n[-•]\s*[^\n]+)*)/i);
  const costMatch = text.match(/estimated cost[^:]*:\s*([^\n]+)/i);
  const reasonMatch = text.match(/reason(?:ing)?[:\s\n]+([^\n]+)/i);
  const extractList = (raw: string | undefined): string[] =>
    (raw ?? '').split(/\n|,/).map(s => s.replace(/^[-•*]\s*/, '').trim()).filter(Boolean);
  return {
    primaryEquipment: extractList(primaryMatch?.[1]),
    supportEquipment: extractList(supportMatch?.[1]),
    estimatedCostRange: costMatch?.[1]?.trim() ?? 'Contact vendors for quotes',
    reasoning: reasonMatch?.[1]?.trim() ?? text.split('\n')[0] ?? '',
  };
}

@Component({
  selector: 'app-ai-drawer',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './ai-drawer.component.html',
  styleUrls: ['./ai-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiDrawerComponent {
  @Input() isOpen = false;
  @Output() closed = new EventEmitter<void>();
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;

  thinkingMessage = 'Reviewing project requirements...';
  inputFocused = false;

  messages: AiMessage[] = [{
    role: 'ai',
    text: "Hi! I'm your Aluguloo AI Equipment Consultant. Tell me about your project and I'll recommend the best equipment available.",
    timestamp: new Date(),
  }];

  inputText = '';
  isTyping = false;

  refinementData: ProjectRefinementData = {
    duration: '', soilType: '', emirate: '', operatorRequired: '', fuelIncluded: '',
  };
  activeRefinementIndex: number | null = null;

  readonly EMIRATES = ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain'];
  readonly DURATIONS = ['1 Week', '1 Month', '3 Months', 'Custom'];
  readonly SOIL_TYPES = ['Soft', 'Mixed', 'Rocky'];

  private readonly _thinkingMessages = [
    'Reviewing project requirements...',
    'Identifying equipment categories...',
    'Matching marketplace inventory...',
    'Preparing recommendations...',
  ];
  private _thinkingInterval: any;

  constructor(private aiService: AiService, private router: Router, private cdr: ChangeDetectorRef) { }

  // ── SCROLL ─────────────────────────────────────────
  private _scrollToBottom(force = false) {
    if (!this.messagesContainer) return;
    const el: HTMLElement = this.messagesContainer.nativeElement;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
    if (force || isNearBottom) {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    }
  }

  // ── AVATAR GROUPING ────────────────────────────────
  showAvatar(index: number): boolean {
    const msg = this.messages[index];
    if (msg.role !== 'ai') return false;
    if (index === 0) return true;
    return this.messages[index - 1].role !== 'ai';
  }

  // ── SEND ────────────────────────────────────────────
  send() {
    const text = this.inputText.trim();
    if (!text || this.isTyping) return;
    this.inputText = '';
    this.messages.push({ role: 'user', text, timestamp: new Date() });
    this.cdr.markForCheck();
    setTimeout(() => this._scrollToBottom(true), 30);
    this._callAi(text, false);
  }

  // ── EMIRATE PILL SELECTION (location prompt) ────────
  selectEmirate(emirate: string) {
    this.messages.push({ role: 'user', text: emirate, timestamp: new Date() });
    this.cdr.markForCheck();
    setTimeout(() => this._scrollToBottom(true), 30);
    this._callAi(emirate, false);
  }

  private _callAi(query: string, isRefinement = false) {
    this.isTyping = true;
    this.cdr.markForCheck();
    setTimeout(() => this._scrollToBottom(true), 30);

    let msgIndex = 0;
    this.thinkingMessage = this._thinkingMessages[msgIndex];
    if (this._thinkingInterval) clearInterval(this._thinkingInterval);
    this._thinkingInterval = setInterval(() => {
      msgIndex = (msgIndex + 1) % this._thinkingMessages.length;
      this.thinkingMessage = this._thinkingMessages[msgIndex];
      this.cdr.markForCheck();
    }, 2000);

    // Build conversation history context
    const historyPayload = this.messages
      .filter(m => m.text)
      .map(m => ({
        role: m.role,
        text: m.text
      }));

    this.aiService.sendMessage(query, historyPayload).subscribe({
      next: (response) => {
        clearInterval(this._thinkingInterval);
        this._thinkingInterval = null;

        const text = response.text;
        const results = response.results ?? [];
        const intent = response.intent;
        const backendGroups = response.equipmentGroups;
        const isMatchmaker = intent === 'project_matchmaker';

        const msg: AiMessage = {
          role: 'ai',
          text,
          timestamp: new Date(),
          equipmentResults: results,
        };

        if (response.needsLocation) {
          msg.awaitingLocation = {
            projectType: response.parsed?.projectType || 'Project',
            rawQuery: query
          };
        }

        if (isRefinement) {
          msg.refinedResponse = parseRefinedResponse(text);
        } else if (isMatchmaker && backendGroups && backendGroups.length > 0) {
          msg.recommendationCard = this._buildCardFromBackendGroups(
            backendGroups,
            response.parsed?.projectType,
            response.parsed?.location,
            response.recommendationSummary,
            response.recommendedCount,
            response.availableCount
          ) ?? undefined;
          msg.showRefinementCard = true;
        }

        if (msg.recommendationCard && response.rentalSummary) {
          (msg.recommendationCard as any).rentalSummary = response.rentalSummary;
        }

        this.messages.push(msg);
        this.isTyping = false;
        this.cdr.markForCheck();
        setTimeout(() => this._scrollToBottom(), 80);
      },
      error: () => {
        clearInterval(this._thinkingInterval);
        this._thinkingInterval = null;
        this.messages.push({ role: 'ai', text: 'Sorry, something went wrong. Please try again.', timestamp: new Date() });
        this.isTyping = false;
        this.cdr.markForCheck();
        setTimeout(() => this._scrollToBottom(), 80);
      },
    });
  }

  // ── REFINEMENT ──────────────────────────────────────
  setActiveRefinement(index: number) {
    this.activeRefinementIndex = index;
    this.refinementData = { duration: '', soilType: '', emirate: '', operatorRequired: '', fuelIncluded: '' };
    this.cdr.markForCheck();
    setTimeout(() => this._scrollToBottom(true), 80);
  }

  submitRefinement(msgIndex: number) {
    const msg = this.messages[msgIndex] as AiMessage;
    const projectType = msg.recommendationCard?.projectType ?? 'project';
    const d = this.refinementData;
    (this.messages[msgIndex] as AiMessage).refinementSubmitted = true;
    this.activeRefinementIndex = null;
    const parts = [
      `Refine equipment fleet recommendation for a ${projectType} project.`,
      d.duration ? `Project duration: ${d.duration}.` : '',
      d.soilType ? `Soil type: ${d.soilType}.` : '',
      d.emirate ? `Location: ${d.emirate}, UAE.` : '',
      d.operatorRequired ? `Operator required: ${d.operatorRequired}.` : '',
      d.fuelIncluded ? `Fuel included: ${d.fuelIncluded}.` : '',
    ];
    this.messages.push({
      role: 'user',
      text: `Refining for: ${[d.duration, d.soilType, d.emirate, d.operatorRequired && 'With Operator', d.fuelIncluded && 'Fuel Included'].filter(Boolean).join(' · ')}`,
      timestamp: new Date()
    });
    this._callAi(parts.filter(Boolean).join(' '), true);
  }

  viewListing(id: string) { this.router.navigate(['/booking-detail', id]); }
  viewEquipmentType(type: string, emirate?: string) {
    const queryParams: Record<string, string> = { what: type };
    if (emirate) queryParams['emirate'] = emirate;
    this.router.navigate(['/category', 'all'], { queryParams });
  }

  // ── CARD BUILDER ───────────────────────────────────
  private _buildCardFromBackendGroups(
    groups: any[],
    projectTypeRaw?: string,
    explicitLocation?: string,
    recommendationSummary?: string,
    recommendedCount?: number,
    availableCount?: number
  ): ProjectRecommendationCard | null {
    if (!groups || groups.length === 0) return null;
    const projectType = projectTypeRaw
      ? projectTypeRaw.charAt(0).toUpperCase() + projectTypeRaw.slice(1)
      : 'Construction';
    const projectTitle = projectType.toLowerCase().includes('construction') ? projectType : `${projectType} Construction`;

    const cardGroups: EquipmentGroup[] = groups
      .filter((g: any) => g.bestMatch)
      .map((g: any) => ({
        type: g.type,
        bestMatch: g.bestMatch as Equipment,
        alternativeCount: g.alternativeCount || 0,
        whyNeeded: g.whyNeeded,
      }));

    if (cardGroups.length === 0) return null;

    return {
      projectType,
      projectTitle,
      groups: cardGroups,
      location: explicitLocation,
      recommendationSummary,
      recommendedCount,
      availableCount,
    };
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); this.send(); }
  }

  useSuggestion(prompt: string) { this.inputText = prompt; this.send(); }
  asEquipmentList(items: unknown): Equipment[] { return (items as Equipment[]) ?? []; }
  refinementValid(): boolean {
    const d = this.refinementData;
    return !!(d.duration || d.soilType || d.emirate || d.operatorRequired || d.fuelIncluded);
  }
  trackByIndex(i: number) { return i; }
  trackByEquipment(_: number, item: Equipment) { return item.id || item.name; }
  trackByGroup(_: number, grp: EquipmentGroup) { return grp.type; }
}